import React from "react";
import { useEffect, useState } from "react";
import { specializationAPI } from "../services/api";
import { useNavigate, useLocation } from "react-router-dom";
import API_BASE_URL from "../config/api";
import LoadingScreen from "../components/LoadingScreen";
import { useSEO } from "../utils/useSEO";
import { useLang } from "../context/LanguageContext";
import "../styles/SpecializationPage.css";
import { Helmet } from "react-helmet-async";

const SpecializationPage = () => {
  useSEO("specialization");
  const { nl } = useLang();
  const L = (obj, field) => (nl && obj?.[field + '_nl']) || obj?.[field] || '';
  const [specializations, setSpecializations] = useState([]);
  const [banner, setBanner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [carouselIndex, setCarouselIndex] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const [seoData, setSeoData] = useState(null);

  // Scroll to section when hash is present and data is loaded
  useEffect(() => {
    if (!loading && location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [loading, location.hash]);

  const toggleSection = (specId, sectionIndex) => {
    const key = `${specId}-${sectionIndex}`;
    setExpandedSections((prev) => {
      // If already open, close it. Otherwise open it and close all others for this spec.
      const isOpen = prev[key];
      const newState = {};
      if (!isOpen) {
        newState[key] = true;
      }
      return newState;
    });
  };

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/seo/services`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setSeoData(data.data);
        }
      })
      .catch((err) => console.error("SEO fetch error:", err));
  }, []);

  const handleNext = (specId, totalSections) => {
    setCarouselIndex((prev) => ({
      ...prev,
      [specId]: Math.min((prev[specId] || 0) + 3, totalSections - 3),
    }));
  };

  const handlePrev = (specId) => {
    setCarouselIndex((prev) => ({
      ...prev,
      [specId]: Math.max((prev[specId] || 0) - 3, 0),
    }));
  };

  useEffect(() => {
    if (!document.querySelector('link[href*="style.css"]')) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "/css/style.css";
      document.head.appendChild(link);
    }

    const fetchData = async () => {
      try {
        const [specsData, bannerResponse] = await Promise.all([
          specializationAPI.getAll(),
          fetch(`${API_BASE_URL}/api/specializations/banner`),
        ]);
        const bannerData = await bannerResponse.json();

        setSpecializations(specsData);
        if (bannerData.success) {
          setBanner(bannerData.data);
        }
        setLoading(false);
      } catch (err) {
        console.error("Error loading data:", err);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <LoadingScreen />;

  console.log("SEO: ", seoData);

  return (
    <>
      <Helmet>
        <title>{seoData?.title || 'Aurelia Technology'}</title>
        {seoData?.description && <meta name="description" content={seoData.description} />}
        {seoData?.keywords && <meta name="keywords" content={seoData.keywords} />}
        {seoData?.canonicalUrl && <link rel="canonical" href={seoData.canonicalUrl} />}
        <meta property="og:title" content={seoData?.ogTitle || seoData?.title || 'Aurelia Technology'} />
        <meta property="og:description" content={seoData?.ogDescription || seoData?.description || ''} />
        {seoData?.ogImage && <meta property="og:image" content={seoData.ogImage} />}
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData?.twitterTitle || seoData?.ogTitle || seoData?.title || 'Aurelia Technology'} />
        <meta name="twitter:description" content={seoData?.twitterDescription || seoData?.ogDescription || seoData?.description || ''} />
        {(seoData?.twitterImage || seoData?.ogImage) && <meta name="twitter:image" content={seoData.twitterImage || seoData.ogImage} />}
      </Helmet>
      <section
        className="contact-banner"
        style={{
          backgroundImage: banner?.image
            ? `url(${banner.image})`
            : "url(https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1200&h=400&fit=crop)",
        }}
      >
        <div className="banner-overlay"></div>
        <div className="banner-content">
          <div className="banner-left">
            <h1>{L(banner, 'title') || (nl ? "ONZE SPECIALISATIES" : "OUR SPECIALIZATIONS")}</h1>
            <p>
              {L(banner, 'subtitle') ||
                (nl ? "Ontdek ons uitgebreide aanbod van technologische specialisaties en expertisegebieden" : "Discover our comprehensive range of technology specializations and expertise areas")}
            </p>
          </div>
        </div>
      </section>

      <section className="specializations">
        <div className="container">
          <div className="section-header">
            <h2>{L(banner, 'mainSectionTitle') || (nl ? "Technologische Expertise" : "Technology Expertise")}</h2>
            <p>
              {L(banner, 'mainSectionDescription') ||
                (nl ? "Geavanceerde oplossingen in meerdere domeinen" : "Cutting-edge solutions across multiple domains")}
            </p>
            <div className="section-divider">
              <div className="divider-line"></div>
              <div className="divider-dots">
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
              </div>
            </div>
          </div>

          <div className="specializations-list">
            {specializations.map((spec, index) => (
              <div key={spec._id || index} id={`spec-${spec._id}`} className="specialization-item">
                <div className="spec-image" style={{ position: "relative" }}>
                  <img src={spec.image} alt={L(spec, 'title')} />
                  <div
                    className="spec-category spec-category-overlay"
                    style={{
                      position: "absolute",
                      top: "10px",
                      right: "10px",
                      padding: "4px 8px",
                      backgroundColor: "rgba(0,0,0,0.7)",
                      color: "white",
                      borderRadius: "4px",
                      fontSize: "12px",
                      display: "none",
                    }}
                  >
                    {nl ? "Specialisatie" : "Specialization"}
                  </div>
                </div>
                <div className="spec-content">
                  <div className="spec-header">
                    <div className="spec-category spec-category-normal">
                      {nl ? "Specialisatie" : "Specialization"}
                    </div>
                    <h3>{L(spec, 'title')}</h3>
                    <p
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        lineHeight: "1.4",
                      }}
                    >
                      {L(spec, 'description')}
                    </p>
                  </div>

                  {spec.sections && spec.sections.length > 0 && (
                    <div className="spec-sections">
                      <h4>{nl ? "Kerngebieden" : "Key Areas"}</h4>
                      <div className="sections-container">
                        <div className="sections-list">
                          {spec.sections
                            .slice(
                              carouselIndex[spec._id] || 0,
                              (carouselIndex[spec._id] || 0) + 3,
                            )
                            .map((section, sIndex) => {
                              const actualIndex =
                                (carouselIndex[spec._id] || 0) + sIndex;
                              const key = `${spec._id}-${actualIndex}`;
                              const isExpanded = expandedSections[key];

                              return (
                                <div key={sIndex} className="section-item">
                                  <div
                                    className="section-title"
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      cursor: "pointer",
                                    }}
                                    onClick={() =>
                                      toggleSection(spec._id, actualIndex)
                                    }
                                  >
                                    <h5>{L(section, 'sectionTitle')}</h5>
                                    <button
                                      style={{
                                        background: "#B28744",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "50%",
                                        width: "24px",
                                        height: "24px",
                                        cursor: "pointer",
                                        fontSize: "16px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      {isExpanded ? "−" : "+"}
                                    </button>
                                  </div>
                                  {isExpanded && (
                                    <div
                                      className="section-description"
                                      style={{ marginTop: "8px" }}
                                    >
                                      <p
                                        style={{
                                          color: "#666",

                                          lineHeight: "1.6",
                                        }}
                                      >
                                        {L(section, 'sectionDescription')?.split(
                                          ".",
                                        )[0] + "." ||
                                          (nl ? "Geen beschrijving beschikbaar" : "No description available")}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                        </div>
                      </div>
                      {spec.sections.length > 3 && (
                        <div className="carousel-controls">
                          <button
                            className="carousel-control-btn"
                            onClick={() => handlePrev(spec._id)}
                            disabled={(carouselIndex[spec._id] || 0) === 0}
                          >
                            ‹
                          </button>
                          <button
                            className="carousel-control-btn"
                            onClick={() =>
                              handleNext(spec._id, spec.sections.length)
                            }
                            disabled={
                              (carouselIndex[spec._id] || 0) >=
                              spec.sections.length - 3
                            }
                          >
                            ›
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default SpecializationPage;
